import { useState } from "react";

import {
  faBars,
  faCalendarDays,
  faCrown,
  faHome,
  faPhone,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation } from "react-router-dom";

import { useLanguage } from "../../contexts/LanguageContext";
import { useUser } from "../../contexts/UserContext";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import PricingModal from "../PricingModal/PricingModal";

import "./Header.css";

const Header = ({ fullHeader = true, showLanguageSelector = false }) => {
  const location = useLocation();
  const { user, userEvents, logout } = useUser();
  const { t } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          <nav className="nav">
            <Link
              className="logo-link"
              to="/"
            >
              <img
                className="sn-wordmark-logo"
                src="/assets/brand/snappin-lockup-dark-450.png"
                alt="Snappin"
              />
            </Link>

            {fullHeader && (
              <>
                {user?.name && (
                  <div className="header-premium-mobile">
                    <FontAwesomeIcon
                        className={`premium-crown ${user.isPremium ? "premium" : "free"}`}
                        icon={faCrown}
                      />
                    <span>{user.name}!</span>
                  </div>
                )}
                {/* Desktop Menu */}
                <ul className="nav-menu">
                  <li>
                    <Link
                      to="/"
                      className={location?.pathname === "/" ? "active" : ""}
                    >
                      {t("header.home")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className={
                        location?.pathname === "/contact" ? "active" : ""
                      }
                    >
                      {t("header.contact")}
                    </Link>
                  </li>
                </ul>
                <div className="nav-cta">
                  {user ? (
                    <>
                      <Link to="/profile" className="user-info" aria-label={t("profile.title")} aria-current={location.pathname === "/profile" ? "page" : undefined}>
                        <span className="user-greeting">
                          <FontAwesomeIcon
                            icon={faCrown}
                            className={`premium-crown ${
                              user.isPremium ? "premium" : "free"
                            }`}
                          />
                          {t("profile.title")} · {user.name}
                        </span>
                      </Link>
                      <Link
                        to="/events/dashboard"
                        className="btn btn-secondary btn-sm"
                      >
                        {t("header.myEvents")} ({userEvents?.length || 0})
                      </Link>
                      <button
                        onClick={logout}
                        className="btn btn-outline btn-sm"
                      >
                        {t("header.logout")}
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/auth" className="btn btn-outline">
                        {t("header.signIn")}
                      </Link>
                      <Link to="/create-event" className="btn btn-primary">
                        {t("header.createEvent")}
                      </Link>
                    </>
                  )}
                  {!user?.isPremium && (
                    <button
                      className="btn-pro"
                      onClick={() => setIsPricingModalOpen(true)}
                    >
                      <FontAwesomeIcon
                        icon={faCrown}
                        color="#9aa0ad"
                        style={{ marginRight: "0.5rem" }}
                      />
                      {}
                      {t("header.upgrade")}
                    </button>
                  )}
                  <LanguageSelector />
                </div>

                {/* Mobile Menu Button */}
                <button
                  className="mobile-menu-toggle"
                  onClick={toggleMobileMenu}
                  aria-label="Toggle menu"
                >
                  <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
                </button>
              </>
            )}
            {showLanguageSelector && <LanguageSelector />}
          </nav>
        </div>
      </header>

      {/* Mobile Sidebar Menu */}
      {fullHeader && (
        <>
          <div
            className={`mobile-menu-overlay ${
              isMobileMenuOpen ? "active" : ""
            }`}
            onClick={closeMobileMenu}
          ></div>
          <div className={`mobile-sidebar ${isMobileMenuOpen ? "active" : ""}`}>
            <div className="mobile-sidebar-header">
              <div
                style={{
                  display: "flex",
                  width: "100%",
                }}
              >
                <img
                  className="sn-wordmark-logo"
                  src="/assets/brand/snappin-lockup-dark-450.png"
                  alt="Snappin"
                />
              </div>
              <button className="mobile-menu-close" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <nav className="mobile-sidebar-nav">
              {!user?.isPremium && (
                <button
                  className="btn-pro mobile-btn-pro"
                  onClick={() => {
                    setIsPricingModalOpen(true);
                    closeMobileMenu();
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCrown}
                    color="#9aa0ad"
                    style={{ marginRight: "0.5rem" }}
                  />
                  {}
                  {t("header.upgrade")}
                </button>
              )}
              <Link
                to="/"
                className={`mobile-nav-link ${
                  location?.pathname === "/" ? "active" : ""
                }`}
                onClick={closeMobileMenu}
              >
                <FontAwesomeIcon icon={faHome} />
                {t("header.home")}
              </Link>
              <Link
                to="/contact"
                className={`mobile-nav-link ${
                  location?.pathname === "/contact" ? "active" : ""
                }`}
                onClick={closeMobileMenu}
              >
                <FontAwesomeIcon icon={faPhone} />
                {t("header.contact")}
              </Link>

              {user && (
                <>
                  <div className="mobile-nav-divider"></div>
                  <Link to="/profile" className={`mobile-nav-link ${location.pathname === "/profile" ? "active" : ""}`} onClick={closeMobileMenu} aria-current={location.pathname === "/profile" ? "page" : undefined}>{t("profile.title")}</Link>
                  <Link
                    to="/events/dashboard"
                    className={`mobile-nav-link ${
                      location?.pathname.includes("/events/dashboard")
                        ? "active"
                        : ""
                    }`}
                    onClick={closeMobileMenu}
                  >
                    <span className="mobile-nav-icon"><FontAwesomeIcon icon={faCalendarDays} /></span>
                    {t("header.myEvents")} ({userEvents?.length || 0})
                  </Link>
                </>
              )}
              <div className="mobile-language-selector">
                <LanguageSelector />
              </div>
            </nav>

            <div className="mobile-sidebar-footer">
              {user ? (
                <div className="mobile-user-section">
                  <div className="mobile-user-info">
                    <span className="mobile-user-greeting">
                      <FontAwesomeIcon
                        icon={faCrown}
                        className={`premium-crown ${
                          user.isPremium ? "premium" : "free"
                        }`}
                      />
                      {user.name}!
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                    className="mobile-logout-btn"
                  >
                    {t("header.logout")}
                  </button>
                </div>
              ) : (
                <div className="mobile-auth-buttons">
                  <Link
                    to="/auth"
                    className="mobile-auth-btn outline"
                    onClick={closeMobileMenu}
                  >
                    {t("header.signIn")}
                  </Link>
                  <Link
                    to="/create-event"
                    className="mobile-auth-btn primary"
                    onClick={closeMobileMenu}
                  >
                    {t("header.createEvent")}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <PricingModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />
    </>
  );
};

export default Header;
